import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useOnboardingCheck } from '../Onboarded';
import { useConciergeCheck } from '../IsConcierge';
import { useUserProfile } from '../UserProfileContext';
import { useTheme } from '../hooks/useTheme';
import { usePrivacyMode } from '../hooks/usePrivacyMode';
import { useDensity } from '../hooks/useDensity';
import { CommandPalette, type Command } from './CommandPalette';

type NavbarControlsProps = {
  isConcierge: boolean;
  isOnboarded: boolean;
};

/**
 * Quick-action cluster — ⌘K command palette, table-density toggle,
 * dark-mode toggle, hide-balances toggle. Matches the onboarding platform's
 * cluster (Phosphor-style two-glyph icons, revealed by CSS state).
 */
const NavbarControls: React.FC<NavbarControlsProps> = ({ isConcierge, isOnboarded }) => {
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const { hidden, toggle: togglePrivacy } = usePrivacyMode();
  const { compact, toggle: toggleDensity } = useDensity();
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const isDark = theme === 'dark';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setCmdkOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const commands: Command[] = [
    { label: 'Dashboard', run: () => navigate('/') },
    { label: 'Profile', run: () => navigate('/profile') },
    ...(!isConcierge ? [{ label: 'Messages', run: () => navigate('/messages') }] : []),
    ...(isConcierge ? [{ label: 'Concierge Dashboard', run: () => navigate('/concierge-dashboard') }] : []),
    ...(isOnboarded && !isConcierge
      ? [
          { label: 'Invoices', run: () => navigate('/invoices') },
          { label: 'Accounting', run: () => navigate('/accounting') },
        ]
      : []),
    { label: isDark ? 'Switch to light mode' : 'Switch to dark mode', run: toggleTheme },
    { label: hidden ? 'Show balances' : 'Hide balances', run: togglePrivacy },
    { label: compact ? 'Comfortable rows' : 'Compact rows', run: toggleDensity },
  ];

  return (
    <div className="dag-navbar-actions flex items-center gap-2">
      <button
        type="button"
        className="dag-iconbtn dag-iconbtn--ghost dag-iconbtn--icon dag-iconbtn--deskonly"
        title="Search (⌘K)"
        aria-label="Open command palette"
        onClick={() => setCmdkOpen(true)}
      >
        <svg className="dag-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" /></svg>
      </button>
      <button
        type="button"
        className="dag-iconbtn dag-iconbtn--ghost dag-iconbtn--icon dag-iconbtn--deskonly"
        aria-pressed={compact}
        title={compact ? 'Comfortable rows' : 'Compact rows'}
        aria-label="Toggle table density"
        onClick={toggleDensity}
      >
        <svg className="dag-ic dag-ic--rows" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></svg>
        <svg className="dag-ic dag-ic--list" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="10" x2="20" y2="10" /><line x1="9" y1="14" x2="20" y2="14" /><line x1="9" y1="18" x2="20" y2="18" /><circle cx="5" cy="6" r=".7" fill="currentColor" stroke="none" /><circle cx="5" cy="10" r=".7" fill="currentColor" stroke="none" /><circle cx="5" cy="14" r=".7" fill="currentColor" stroke="none" /><circle cx="5" cy="18" r=".7" fill="currentColor" stroke="none" /></svg>
      </button>
      <button
        type="button"
        className="dag-iconbtn dag-iconbtn--ghost dag-iconbtn--icon"
        aria-pressed={isDark}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        onClick={toggleTheme}
      >
        <svg className="dag-ic dag-ic--moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12.6A8.5 8.5 0 1 1 11.4 3a6.6 6.6 0 0 0 9.6 9.6Z" /></svg>
        <svg className="dag-ic dag-ic--sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4.2" /><line x1="12" y1="2.6" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="21.4" /><line x1="2.6" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="21.4" y2="12" /><line x1="5.4" y1="5.4" x2="7.1" y2="7.1" /><line x1="16.9" y1="16.9" x2="18.6" y2="18.6" /><line x1="18.6" y1="5.4" x2="16.9" y2="7.1" /><line x1="7.1" y1="16.9" x2="5.4" y2="18.6" /></svg>
      </button>
      <button
        type="button"
        className="dag-iconbtn"
        aria-pressed={hidden}
        title={hidden ? 'Show balances' : 'Hide balances'}
        aria-label={hidden ? 'Show balances' : 'Hide balances'}
        onClick={togglePrivacy}
      >
        <svg className="dag-ic dag-ic--eye" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></svg>
        <svg className="dag-ic dag-ic--eye-off" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2.5 12S6 5.5 12 5.5a9.7 9.7 0 0 1 5.2 1.5" /><path d="M21.5 12s-1.3 2.4-3.7 4.1" /><path d="M9.7 9.8A3 3 0 0 0 12 15a3 3 0 0 0 2.2-1" /><line x1="3.5" y1="3.5" x2="20.5" y2="20.5" /></svg>
        <span className="dag-iconbtn__label">{hidden ? 'Show balances' : 'Hide balances'}</span>
      </button>
      <CommandPalette open={cmdkOpen} commands={commands} onClose={() => setCmdkOpen(false)} />
    </div>
  );
};

const Navbar: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, logout, user } = useAuth0();
  const { profile, isLoading: isLoadingProfile } = useUserProfile();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState<boolean>(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // REACT_APP_ENV is a build-time constant — derive it; no state/effect needed.
  const environment = process.env.REACT_APP_ENV || "prod";
  const onboarded = useOnboardingCheck();
  const [isOnboarded, setIsOnboarded] = useState<boolean>(false);
  const isConcierge = useConciergeCheck();

  const displayName = profile?.displayName || "";

  useEffect(() => {
    onboarded.then(o => {
      setIsOnboarded(o);
    })
  }, [onboarded]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
      setIsUserMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen, handleClickOutside]);

  return (
    <nav className="bg-white shadow-lg dag-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex-shrink-0 flex items-center"
              aria-label="Digital Asset Group — Dashboard"
            >
              <img
                src="/dag-wealth-dark.svg"
                alt="Digital Asset Group"
                className="dag-logo--for-light h-9 w-auto mr-3"
              />
              <img
                src="/dag-wealth-light.svg"
                alt=""
                aria-hidden="true"
                className="dag-logo--for-dark h-9 w-auto mr-3"
              />
              <h1 className="text-xl font-bold text-gray-900 dag-wordmark">DFO Portal</h1>
            </Link>
            {environment && environment === "dev" &&
              <span className="bg-green-600 text-white mx-2 px-3 py-1 rounded-full text-sm font-semibold">
                dev
              </span>
            }
          </div>

          <div className="flex items-center gap-3">
            <NavbarControls isConcierge={!!isConcierge} isOnboarded={isOnboarded} />

          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Home button disabled for now */}
            {/* <Link
              to="/dashboard"
              className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
            >
              Home
            </Link> */}
            {/* Remove Concierge Dashboard and hide Messages for concierge */}
            {!isConcierge && (
              <Link
                to="/messages"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Messages
              </Link>
            )}
            {isConcierge && (
              <Link
                to="/concierge-dashboard"
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Concierge Dashboard
              </Link>
            )}
            {isOnboarded && !isConcierge && (
              <>
                <Link
                  to="/invoices"
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Invoices
                </Link>
                <Link
                  to="/accounting"
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Accounting
                </Link>
              </>
            )}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                  className="dag-user-menu__trigger flex items-center gap-2 text-sm px-3 py-2 rounded-md font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none"
                >
                  {isLoadingProfile ? (
                    <span className="inline-block bg-gray-200 animate-pulse rounded w-32 h-4" />
                  ) : (
                    <span>Welcome, {displayName || user?.name || user?.email}</span>
                  )}
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isUserMenuOpen && (
                  <div
                    role="menu"
                    className="dag-user-menu animate-scale-in origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-2xl p-1.5 z-50"
                  >
                    <Link
                      to="/profile"
                      role="menuitem"
                      className="dag-user-menu__item block px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      role="menuitem"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        logout({ logoutParams: { returnTo: window.location.origin } });
                      }}
                      className="dag-user-menu__item dag-user-menu__item--danger w-full text-left block px-3 py-2 text-sm rounded-md transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => loginWithRedirect()}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Login
              </button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
            {/* Home button disabled for now */}
            {/* <Link
              to="/dashboard"
              className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link> */}
            <Link
              to="/profile"
              className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Profile
            </Link>
            {/* Hide Messages for concierge */}
            {!isConcierge && (
              <Link
                to="/messages"
                className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Messages
              </Link>
            )}
            {isOnboarded && !isConcierge && (
              <>
                <Link
                  to="/invoices"
                  className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Invoices
                </Link>
                <Link
                  to="/accounting"
                  className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Accounting
                </Link>
              </>
            )}
            {isAuthenticated ? (
              <div className="px-3 py-2">
                <span className="text-sm text-gray-700 block mb-2">
                  Welcome, {displayName || user?.name || user?.email}
                </span>
                <button
                  onClick={() => {
                    logout({ logoutParams: { returnTo: window.location.origin } });
                    setIsMenuOpen(false);
                  }}
                  className="btn-remove w-full"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  loginWithRedirect();
                  setIsMenuOpen(false);
                }}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Login
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;